import { data, Link } from 'react-router'
import { resolveDistinctId } from '#app/analytics/attribution.server.ts'
import { type RouteAnalyticsHandle } from '#app/analytics/constants.ts'
import { listingLinkHandler } from '#app/analytics/listing.ts'
import { ClaimEvidence } from '#app/components/claim-evidence.tsx'
import { Dateline } from '#app/components/dateline.tsx'
import { LicensingBlock } from '#app/components/licensing-block.tsx'
import { QuoteRequestForm } from '#app/components/quote-request-form.tsx'
import { RankingTable } from '#app/components/ranking-table.tsx'
import { ScoreMark } from '#app/components/score.tsx'
import { ThemeFilter } from '#app/components/theme-filter.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Separator } from '#app/components/ui/separator.tsx'
import { loadClaimsForBusinesses } from '#app/scoring/claims.server.ts'
import {
	RANK_ORDER,
	RANKING_ELIGIBLE,
	getPublishableCitiesForCategory,
} from '#app/scoring/publishable-pages.server.ts'
import { getRatingConcentration } from '#app/scoring/rating-concentration.server.ts'
import { describeRatingConcentration } from '#app/scoring/rating-concentration.ts'
import {
	CORPUS_MIN_REVIEWS,
	DEPTH_FLOORS,
	RANK_LIMIT,
	clearsRankingFloor,
} from '#app/scoring/scoring-model.ts'
import { authorReference, buildPersonLd } from '#app/seo/author.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { recordListingImpressions } from '#app/utils/business-metrics.server.ts'
import { categoryPlural } from '#app/utils/category-name.ts'
import { prisma } from '#app/utils/db.server.ts'
import { PAGE_MEASURE } from '#app/utils/layout.ts'
import { formatPhoneNumber, generateSlug, pluralize } from '#app/utils/misc.tsx'
import { getNewestReviewTimestamp } from '#app/utils/newest-review.server.ts'
import { quoteRequestAction } from '#app/utils/quote-request.server.ts'
import { type Route } from './+types/$categorySlug.ts'

/**
 * Metro-wide ranking page: /best/{category}
 *
 * Ranking order is never stored — it derives from Business.score at render
 * time, so a rescore is reflected immediately while the editorial shell in
 * RankingPage stays hand-editable.
 *
 * The depth floor is enforced here rather than in the sitemap alone: a page
 * that cannot show a real field returns 404 instead of publishing a two-item
 * "ranking".
 */

/**
 * Businesses a facet must cover before it is offered.
 *
 * A filter that leaves two results is not a useful view, it is a dead end.
 */
const MIN_FACET_BUSINESSES = 3

/**
 * Cities named in the strip near the top. Past this it stops being a glance
 * and becomes the full list, which already exists further down the page.
 */
const CITY_STRIP_LIMIT = 8

export async function loader({ params, request, context }: Route.LoaderArgs) {
	const { categorySlug } = params
	/*
	 * Multiple themes, combined with AND.
	 *
	 * A category can surface dozens of themes — 85 on roofing — and a
	 * single-select filter turns that into a list you page through one option
	 * at a time. Narrowing is the useful operation: "mentions insurance claims
	 * AND cleanup" is a real question a reader has, while OR just grows the
	 * list they were already looking at.
	 */
	const themeFilters = new URL(request.url).searchParams.getAll('mentions')

	const category = await prisma.category.findUnique({
		where: { slug: categorySlug },
		select: {
			id: true,
			name: true,
			pluralName: true,
			slug: true,
			// Regulatory facts for the trade. Nullable throughout: the block
			// renders nothing until someone has researched the category.
			licensingStatus: true,
			licensingBoard: true,
			licensingBoardUrl: true,
			licensingVerifyUrl: true,
			licensingNote: true,
		},
	})

	if (!category) {
		throw new Response('Category not found', { status: 404 })
	}

	/*
	 * The full scored set, not just the ranked page. Facet counts have to be
	 * computed across every eligible business, otherwise a theme would appear
	 * to cover three businesses when it actually covers twelve.
	 */
	const allScored = await prisma.business.findMany({
		where: {
			...RANKING_ELIGIBLE,
			categories: { some: { id: category.id } },
		},
		orderBy: [...RANK_ORDER],
		select: {
			id: true,
			slug: true,
			name: true,
			score: true,
			scoreComputedAt: true,
			scoreConfidence: true,
			ratingValue: true,
			ratingCount: true,
			reviewsHeld: true,
			bestFor: true,
			whyItRanks: true,
			pros: true,
			watchOuts: true,
			themes: true,
			phone: true,
			website: true,
			domain: true,
			logoUrl: true,
			synthesisClaims: true,
			synthesisAt: true,
			cities: { take: 1, select: { name: true, slug: true } },
		},
	})

	/*
	 * A ranked list needs a real field to rank, and enough of that field
	 * actually measured for the order to mean anything. Without the coverage
	 * check a category with no fetched reviews clears the depth floor and
	 * publishes a ranking that is 58% neutral for every listing.
	 *
	 * Shares clearsRankingFloor with the sitemap so the two cannot disagree.
	 */
	if (
		!clearsRankingFloor({
			total: allScored.length,
			// allScored is already ordered by score desc, so the ranked slice is
			// its head. Coverage is measured there and not across the tail we
			// deliberately never bought reviews for.
			measuredInRankedSlice: allScored
				.slice(0, RANK_LIMIT)
				.filter((business) => (business.reviewsHeld ?? 0) >= CORPUS_MIN_REVIEWS)
				.length,
			depthFloor: DEPTH_FLOORS.bestMetro,
		})
	) {
		throw new Response('Not enough measured businesses to publish a ranking', {
			status: 404,
		})
	}

	const totalScored = allScored.length
	const rankingPage = await prisma.rankingPage.findUnique({
		where: { categoryId_cityKey: { categoryId: category.id, cityKey: '' } },
		select: { title: true, h1: true, intro: true, methodologyNote: true },
	})

	/*
	 * Facets come from the category's own normalized themes, so a roofing page
	 * offers "insurance claims" and a plumbing page offers whatever its own
	 * corpus surfaces. Nothing here is category-specific.
	 *
	 * Keyed by slug rather than theme string, because two themes can slugify
	 * identically. Each facet holds EVERY theme string mapping to its slug, and
	 * both the count and the filter use that whole set. Counting is by distinct
	 * business, not summed per-theme tallies: a business carrying both
	 * "insurance claims" and "insurance-claims" is one result, not two.
	 */
	const themesBySlug = new Map<string, Set<string>>()
	for (const business of allScored) {
		for (const theme of new Set(business.themes)) {
			const slug = generateSlug(theme)
			if (!slug) continue
			const existing = themesBySlug.get(slug)
			if (existing) existing.add(theme)
			else themesBySlug.set(slug, new Set([theme]))
		}
	}

	const matchesFacet = (
		business: { themes: string[] },
		themes: Set<string>,
	): boolean => business.themes.some((theme) => themes.has(theme))

	const facets = [...themesBySlug.entries()]
		.map(([slug, themes]) => ({
			slug,
			// Shortest label: the most general phrasing among the collisions.
			// Alphabetical tie-break keeps the chip stable across rescores, which
			// change Set insertion order.
			label: [...themes].sort(
				(a, b) => a.length - b.length || a.localeCompare(b),
			)[0]!,
			themes,
			count: allScored.filter((b) => matchesFacet(b, themes)).length,
		}))
		.filter((facet) => facet.count >= MIN_FACET_BUSINESSES)
		.sort((a, b) => b.count - a.count)

	/*
	 * Unknown or too-thin themes are dropped rather than erroring: the
	 * unfiltered ranking is always a valid answer to a bad query string.
	 */
	const activeFacets = facets.filter((facet) =>
		themeFilters.includes(facet.slug),
	)

	/*
	 * The theme sets are a loader-internal matching detail. Filtering happens
	 * below, so the client only needs enough to render and link a chip.
	 */
	const publicFacets = facets.map(({ slug, label, count }) => ({
		slug,
		label,
		count,
	}))
	const publicActiveFacets = activeFacets.map(({ slug, label, count }) => ({
		slug,
		label,
		count,
	}))

	/*
	 * An unknown or too-thin theme is treated as no filter rather than an error:
	 * the unfiltered ranking is always a valid answer.
	 *
	 * Filtered lists are capped like the base ranking. Every row carries claims
	 * and their cited reviews, so an uncapped facet matching forty businesses
	 * would load a page nobody reads to the bottom of.
	 */
	const matching = activeFacets.length
		? allScored.filter((b) =>
				activeFacets.every((facet) => matchesFacet(b, facet.themes)),
			)
		: allScored
	const matchingCount = matching.length
	const businesses = matching.slice(0, RANK_LIMIT)

	/*
	 * Everything left to load is independent of everything else here, so it goes
	 * in one round trip rather than four. This is the hot path for every ranking
	 * page, and a serial chain pays the database's latency once per query for no
	 * ordering the page actually needs.
	 */
	const [claimsByBusiness, cities, newestReview, concentration] =
		await Promise.all([
			loadClaimsForBusinesses(businesses),
			/*
			 * Cities with enough scored businesses to justify their own ranking page.
			 * Shared with the sitemap and the city switcher so the three cannot drift.
			 */
			getPublishableCitiesForCategory(category.slug),
			/*
			 * Newest review in this category's corpus. Paired with the last rescore
			 * below as the two dates the dateline states separately. Both are maxima
			 * over the ranked set rather than "now", so neither moves when a job
			 * re-runs over data that did not change.
			 */
			getNewestReviewTimestamp(allScored.map((b) => b.id)),
			/*
			 * Scoped to this category: the claim that stars fail to separate the
			 * market has to be true of the market on screen, not of the catalog
			 * average.
			 */
			getRatingConcentration({ categories: { some: { id: category.id } } }),
		])

	const analyzed = allScored.filter((b) => b.synthesisAt).length
	const reviewsRead = allScored.reduce(
		(sum, b) => sum + (b.reviewsHeld ?? 0),
		0,
	)

	// The second of the two dates the dateline states: the most recent rescore.
	const scoredAt = allScored.reduce<Date | null>(
		(latest, b) =>
			b.scoreComputedAt && (!latest || b.scoreComputedAt > latest)
				? b.scoreComputedAt
				: latest,
		null,
	)

	// Impressions are recorded server-side so they survive ad blockers and so a
	// listing that is seen but never clicked still shows up in the owner's
	// report. Bot traffic is filtered inside the helper, and the write is not
	// awaited — a ranking page must not wait on analytics.
	void recordListingImpressions({
		request,
		// Resolved rather than left to the cookie: on a cookieless first hit the
		// engagement guard would otherwise key this visitor on IP+UA, which is a
		// coarser identity than the one search already uses.
		distinctId: resolveDistinctId({ request, context }),
		businessIds: businesses.map((b) => b.id),
	})

	const concentrationSentence =
		describeRatingConcentration(
			concentration,
			categoryPlural(category).toLowerCase(),
		)?.sentence ?? null

	return data({
		category,
		businesses: businesses.map((b) => ({
			...b,
			claims: claimsByBusiness.get(b.id) ?? { pros: [], watchOuts: [] },
		})),
		rankingPage,
		cities,
		facets: publicFacets,
		activeFacets: publicActiveFacets,
		stats: {
			totalScored,
			analyzed,
			reviewsRead,
			matchingCount,
			concentrationSentence,
			reviewsThrough: newestReview?.toISOString() ?? null,
			scoredAt: scoredAt?.toISOString() ?? null,
		},
	})
}

/**
 * The quote request posts back to the page it was rendered on, so the form
 * works without JavaScript and a failed submission keeps the reader in place
 * with the ranking still on screen.
 */
export async function action({ request, context }: Route.ActionArgs) {
	return quoteRequestAction({ request, context })
}

export const handle = {
	// Read by useRouteAnalytics in root.tsx; tags every pageview and
	// client event fired on this route with its page archetype.
	analytics: { pageType: 'best' } satisfies RouteAnalyticsHandle,
	breadcrumb({ data }: { data: Awaited<ReturnType<typeof loader>>['data'] }) {
		if (!data?.category) return null
		return {
			name: `Best ${categoryPlural(data.category)}`,
			url: `/best/${data.category.slug}`,
		}
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
	}): SeoProps | null {
		if (!loaderData?.category) return null
		const { category, businesses, stats, activeFacets } = loaderData
		// Filtered views canonicalize to the unfiltered ranking. They are a way
		// of reading one ranking, not separate rankings, and indexing them would
		// put near-duplicate pages in competition with their own parent.
		const canonical = `${origin}/best/${category.slug}`
		const plural = categoryPlural(category).toLowerCase()

		const baseTitle =
			loaderData.rankingPage?.title ??
			`Best ${categoryPlural(category)} in DFW, Ranked`

		/*
		 * The description has to match the list actually on the page. Reusing the
		 * base ranking's wording on a filtered view claims the twelve businesses
		 * matching a theme are the highest-scoring in DFW, which is false, and
		 * this same string feeds the OG and Twitter cards.
		 */
		const facetLabels = activeFacets.map((f) => f.label).join(' and ')
		const description = activeFacets.length
			? `${stats.matchingCount} ${plural} in Dallas-Fort Worth whose reviews repeatedly mention ${facetLabels}${
					stats.matchingCount > businesses.length
						? `, showing the top ${businesses.length}`
						: ', ranked by overall score'
				}. Part of our full ${plural} ranking.`
			: `The ${businesses.length} highest-scoring ${plural} in Dallas-Fort Worth, ranked on review sentiment, rating, volume and recency. ${stats.reviewsRead.toLocaleString()} reviews read. No paid placement.`

		const title = activeFacets.length
			? `Best ${categoryPlural(category)} in DFW for ${facetLabels}`
			: baseTitle

		return {
			title,
			description,
			canonical,
			robots: activeFacets.length ? 'noindex, follow' : undefined,
			/*
			 * A card generated from this ranking rather than the sitewide
			 * fallback. Rankings are the most-shared page type here, and a link
			 * that previews with its own headline and scored count is the entire
			 * argument of the page compressed into the one frame a reader sees
			 * before deciding whether to click.
			 *
			 * Always the canonical slug, never the facet variant: a filtered view
			 * canonicalizes to the base page, so it should preview as that page.
			 */
			openGraph: {
				title,
				description,
				type: 'article',
				url: canonical,
				image: `${origin}/og/best/${category.slug}.png`,
			},
			twitter: {
				card: 'summary_large_image',
				title,
				description,
				image: `${origin}/og/best/${category.slug}.png`,
			},
			/*
			 * Structured data describes the canonical ranking only. A filtered
			 * view canonicalizes to the base page, so emitting an ItemList of a
			 * subset under the base page's name would describe something that
			 * does not exist at that URL.
			 */
			/*
			 * The ranking, plus the person responsible for the method behind it.
			 *
			 * The Person node is emitted with a stable @id and the list carries an
			 * author reference to it, so a crawler resolves one author across every
			 * ranking rather than a new entity per page.
			 */
			structuredData: activeFacets.length
				? []
				: [
						// Earned-only: this list reflects computed scores, never payment.
						buildItemListLd({
							name: baseTitle,
							items: businesses.map((b) => ({
								name: b.name,
								url: `${origin}/business/${b.slug}`,
							})),
							author: authorReference({ origin }),
							dateModified: loaderData.stats.scoredAt ?? undefined,
						}),
						buildPersonLd({ origin }),
					],
		}
	},
}

export default function BestCategoryPage({ loaderData }: Route.ComponentProps) {
	const {
		category,
		businesses,
		rankingPage,
		cities,
		facets,
		activeFacets,
		stats,
	} = loaderData
	const plural = categoryPlural(category)

	return (
		<div className={`${PAGE_MEASURE} py-12`}>
			<h1 className="mb-3 font-serif text-3xl tracking-tight md:text-4xl">
				{rankingPage?.h1 ?? `Best ${plural} in Dallas–Fort Worth`}
			</h1>

			<Dateline
				stats={{
					reviewsThrough: stats.reviewsThrough,
					scoredAt: stats.scoredAt,
					scored: stats.totalScored,
					analyzed: stats.analyzed,
					reviewsRead: stats.reviewsRead,
				}}
			/>

			{/*
			 * Prose keeps its own measure inside a wider page.
			 *
			 * The container was widened for the table, the score blocks and the ranked
			 * entries, which are structured content and were cramped. Body copy has the
			 * opposite need: past roughly 75 characters a line gets measurably harder to
			 * track back to the start of the next one. So the page is wide and the
			 * paragraphs are not.
			 */}
			<p className="text-muted-foreground mb-6 max-w-3xl text-lg leading-relaxed">
				{activeFacets.length
					? `${stats.matchingCount} ${plural.toLowerCase()} have reviews that repeatedly mention ${activeFacets.map((f) => f.label).join(' and ')}${stats.matchingCount > businesses.length ? `, and these are the ${businesses.length} highest scoring of them` : ', ranked by overall score'}. Every business here also appears in the full ranking.`
					: (rankingPage?.intro ??
						/*
						 * The rating claim is measured for THIS category rather than
						 * asserted. It previously read "nearly every business in this
						 * market advertises above 4.9 stars" on every category page —
						 * true of the roofing pilot, unverified for the other 105.
						 * Omitted entirely when the category is too small to support it.
						 */
						`These are the ${businesses.length} highest-scoring ${plural.toLowerCase()} of ${stats.totalScored} we track across the DFW metro.${
							stats.concentrationSentence
								? ` ${stats.concentrationSentence}`
								: ' We rank on what reviewers actually wrote, not the star they clicked.'
						}`)}
			</p>

			{/*
			 * Jump to a city, high in the document.
			 *
			 * The full "By city" block sits below fifteen ranked entries with
			 * expandable review evidence, which is the last thing on the page. Those
			 * links are among the most valuable on the site — city x category is
			 * where the search demand is, roughly ten times the metro term — and a
			 * reader who actually wants Fort Worth is still deciding here, not after
			 * reading a metro ranking to the end.
			 *
			 * Deliberately not linked from each ranked entry: those cities are
			 * already linked once below, so per-entry links would add duplicates and,
			 * for cities under the depth floor, redirects.
			 */}
			{cities.length > 0 ? (
				<p className="text-muted-foreground mb-8 text-sm leading-relaxed">
					<span className="text-foreground font-medium">Jump to a city:</span>{' '}
					{cities.slice(0, CITY_STRIP_LIMIT).map((city, index) => (
						<span key={city.slug}>
							{index > 0 ? <span aria-hidden> · </span> : null}
							<Link
								className="hover:text-foreground underline underline-offset-4"
								to={`/${city.slug}/${category.slug}`}
							>
								{city.name}
							</Link>
						</span>
					))}
					{cities.length > CITY_STRIP_LIMIT ? (
						<>
							<span aria-hidden> · </span>
							<a
								className="hover:text-foreground underline underline-offset-4"
								href="#by-city"
							>
								{cities.length - CITY_STRIP_LIMIT} more
							</a>
						</>
					) : null}
				</p>
			) : null}

			{facets.length > 0 ? (
				<ThemeFilter
					basePath={`/best/${category.slug}`}
					facets={facets}
					active={activeFacets}
				/>
			) : null}

			<div className="text-muted-foreground mb-8 flex flex-wrap gap-x-6 gap-y-2 text-sm">
				<span>{stats.totalScored} scored</span>
				<span>{stats.analyzed} with review analysis</span>
				<span>{stats.reviewsRead.toLocaleString()} reviews read</span>
				<Link className="underline underline-offset-4" to="/scoring-method">
					How we score
				</Link>
			</div>

			<RankingTable businesses={businesses} />

			<div className="space-y-8">
				{businesses.map((business, index) => (
					<div key={business.id}>
						{/*
						 * The score is the reason this entry sits where it does, so it
						 * carries the weight of the name rather than sitting beside it as
						 * a chip. `items-stretch` makes the block span the name, the
						 * rating and the best-for tag as one unit.
						 */}
						<div className="mb-3 flex items-stretch gap-4">
							{business.score != null ? (
								<ScoreMark score={business.score} rank={index + 1} size="lg" />
							) : null}
							<div className="min-w-0 flex-1">
								<h2 className="font-serif text-xl tracking-tight md:text-2xl">
									<Link
										className="hover:underline"
										to={`/business/${business.slug}`}
										{...listingLinkHandler('listing_result_clicked', {
											businessId: business.id,
											pageType: 'best',
											categorySlug: category.slug,
											placement: 'ranking_list',
											position: index + 1,
										})}
									>
										{business.name}
									</Link>
								</h2>
								<div className="text-muted-foreground mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
									<span className="font-mono tabular-nums">
										{business.ratingValue?.toFixed(1)}★{' '}
										{(business.ratingCount ?? 0).toLocaleString()} reviews
									</span>
									{business.cities[0] ? (
										<span>{business.cities[0].name}</span>
									) : null}
									{business.reviewsHeld ? (
										<span>{business.reviewsHeld} read in full</span>
									) : null}
								</div>
								{/*
								 * Text, not a Badge.
								 *
								 * The Badge base carries whitespace-nowrap AND shrink-0, so
								 * a bestFor string could neither wrap nor compress and forced
								 * the whole entry wider than a phone viewport — which is what
								 * put a horizontal scrollbar on this page. These run to 69
								 * characters (median 41); they are sentences, and a badge is
								 * for short labels. This also matches how the business page
								 * has always rendered the same field.
								 */}
								{business.bestFor ? (
									<p className="text-muted-foreground mt-2 text-sm">
										Best for: {business.bestFor}
									</p>
								) : null}
							</div>
						</div>

						{business.whyItRanks ? (
							<p className="text-muted-foreground mb-4 max-w-3xl leading-relaxed">
								{business.whyItRanks}
							</p>
						) : null}

						{business.claims.pros.length > 0 ? (
							<div className="mb-3">
								<h3 className="mb-2 text-sm font-medium">
									What reviewers consistently mention
								</h3>
								<ul className="space-y-2">
									{business.claims.pros.map((claim, claimIndex) => (
										<ClaimEvidence key={`pro-${claimIndex}`} claim={claim} />
									))}
								</ul>
							</div>
						) : null}

						{business.claims.watchOuts.length > 0 ? (
							<div className="mb-3">
								<h3 className="mb-2 text-sm font-medium">Watch out for</h3>
								<ul className="space-y-2">
									{business.claims.watchOuts.map((claim, claimIndex) => (
										<ClaimEvidence
											key={`watch-${claimIndex}`}
											claim={claim}
											tone="caution"
										/>
									))}
								</ul>
							</div>
						) : null}

						<div className="mt-4 flex flex-wrap gap-2">
							<Button asChild size="sm" variant="outline">
								<Link to={`/business/${business.slug}`}>View listing</Link>
							</Button>
							{business.phone ? (
								<Button asChild size="sm" variant="outline">
									{/* Dial the raw number, show the formatted one. */}
									<a href={`tel:${business.phone}`}>
										{formatPhoneNumber(business.phone)}
									</a>
								</Button>
							) : null}
						</div>

						{index < businesses.length - 1 ? (
							<Separator className="mt-8" />
						) : null}
					</div>
				))}
			</div>

			{/*
			 * Below the ranking, never above it.
			 *
			 * Above, the ranking reads as bait for a lead funnel and the
			 * reference-work positioning goes with it. The reader gets the answer
			 * they came for first, then the offer.
			 */}
			<QuoteRequestForm
				categoryId={category.id}
				categoryName={category.name}
				sourcePath={`/best/${category.slug}`}
			/>

			<Separator className="my-10" />

			{/*
			 * What the state does and does not check for this trade. Placed after
			 * the ranking because it qualifies the list rather than introducing it:
			 * a reader who has just seen fifteen names is the one who needs to know
			 * nobody licenses them.
			 *
			 * Renders nothing for a category whose licensing has not been
			 * researched, so it is safe on all thirty.
			 */}
			<LicensingBlock
				category={category}
				fallbacks={[
					'Whether the business is still operating, rechecked on a schedule',
					'How many reviews we hold and how far back they go',
					'Whether the owner answers critical reviews',
					'Whether ratings are rising or falling year over year',
				]}
			/>

			{cities.length > 0 ? (
				<div className="mb-8 scroll-mt-24" id="by-city">
					<h2 className="mb-2 font-serif text-lg tracking-tight">By city</h2>
					<p className="text-muted-foreground mb-3 text-sm leading-relaxed">
						Most {plural.toLowerCase()} here work across the metro. These cities
						have enough scored businesses to rank on their own.
					</p>
					{/*
					 * Anchor text carries the query these pages actually compete for.
					 * "Dallas" tells a crawler nothing about the destination, while
					 * "Roofing contractors in Dallas" is the phrasing with roughly ten
					 * times the search volume of the metro "best" term and the string a
					 * model reproduces when citing the page.
					 */}
					<ul className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
						{cities.map((city) => (
							<li key={city.slug}>
								<Link
									className="underline underline-offset-4"
									to={`/${city.slug}/${category.slug}`}
								>
									{plural} in {city.name}
								</Link>{' '}
								<span className="text-muted-foreground tabular-nums">
									({city.count})
								</span>
							</li>
						))}
					</ul>
				</div>
			) : null}

			<div className="text-muted-foreground text-sm leading-relaxed">
				<p className="mb-2">
					Ranked on the{' '}
					<Link className="underline underline-offset-4" to="/scoring-method">
						DFW Professionals scoring method
					</Link>
					: review sentiment, star rating, review volume, recency and profile
					completeness. Every strength and caution above is supported by at
					least two separate reviews, and you can expand any of them to read the
					source reviews.
				</p>
				<p>
					No business paid for a position on this page, and none can. Scores are
					recomputed as new reviews arrive. Spotted something wrong?{' '}
					<Link className="underline underline-offset-4" to="/contact">
						Tell us
					</Link>
					.
				</p>
			</div>
		</div>
	)
}
